import axios, { AxiosError, AxiosInstance } from 'axios';
import { installAuthInterceptors } from '@/lib/auth/axios-auth';

const UPLOAD_PHOTO_PATH = '/api/v1/upload/photo';
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export type MediaUploadErrorKind =
  | 'too_large'
  | 'unsupported_type'
  | 'blocked_by_scan'
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'server'
  | 'network';

export class MediaUploadError extends Error {
  readonly name = 'MediaUploadError';
  readonly kind: MediaUploadErrorKind;
  readonly status: number;

  constructor(kind: MediaUploadErrorKind, status: number, message: string) {
    super(message);
    this.kind = kind;
    this.status = status;
  }
}

export interface PhotoUploadResult {
  success: boolean;
  bucket: string;
  normalized_name: string;
  original_filename: string;
  size: number;
  variants: Record<string, string>;
  metadata: Record<string, unknown>;
  file_url?: string;
  object_key?: string;
  message?: string;
}

export interface PresignedUrlResult {
  success: boolean;
  url: string;
  expires_in: number;
  object_path: string;
}

export async function getPhotoPresignedUrl(
  tenantId: string,
  objectPath: string,
  expiry = 3600,
): Promise<string> {
  const { data } = await client().post<PresignedUrlResult>('/api/v1/presigned-url', {
    bucket_type: 'tenant',
    tenant_id: tenantId,
    object_path: objectPath,
    expiry,
  });
  return data.url;
}

export interface UploadPhotoParams {
  jobId: string;
  tenantId: string;
  file: File;
  originalFilename?: string;
  generateVariants?: boolean;
  /** Base delay between retries, doubled each retry (default 250ms). */
  retryDelayMs?: number;
  /** Max retries on 5xx/network (default 2). */
  maxRetries?: number;
}

function buildClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_URL,
    timeout: 60000,
  });
  installAuthInterceptors(client);
  return client;
}

let _singleton: AxiosInstance | null = null;
function client(): AxiosInstance {
  if (!_singleton) _singleton = buildClient();
  return _singleton;
}

function mapError(err: unknown): MediaUploadError {
  if (err instanceof AxiosError && err.response) {
    const { status } = err.response;
    const detail = (err.response.data as { detail?: string } | undefined)?.detail;
    const message = detail ?? err.message ?? 'Upload failed.';
    const kind: MediaUploadErrorKind =
      status === 413
        ? 'too_large'
        : status === 415
          ? 'unsupported_type'
          : status === 403
            ? 'blocked_by_scan'
            : status === 401
              ? 'unauthorized'
              : status === 400
                ? 'bad_request'
                : status >= 500
                  ? 'server'
                  : 'forbidden';
    return new MediaUploadError(kind, status, message);
  }
  const raw = err instanceof Error ? err.message : 'Network error';
  return new MediaUploadError('network', 0, raw);
}

function shouldRetry(err: MediaUploadError): boolean {
  return err.kind === 'server' || err.kind === 'network';
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function uploadPreInspectionPhoto(
  params: UploadPhotoParams,
): Promise<PhotoUploadResult> {
  const {
    jobId,
    tenantId,
    file,
    originalFilename,
    generateVariants = true,
    retryDelayMs = 250,
    maxRetries = 2,
  } = params;

  const form = new FormData();
  form.append('file', file);
  form.append('tenant_id', tenantId);
  form.append('job_id', jobId);
  if (originalFilename) form.append('original_filename', originalFilename);
  form.append('generate_variants', String(generateVariants));

  let attempt = 0;
  let lastErr: MediaUploadError | null = null;
  while (attempt <= maxRetries) {
    try {
      const { data } = await client().post<PhotoUploadResult>(
        UPLOAD_PHOTO_PATH,
        form,
      );
      return data;
    } catch (err) {
      const mapped = mapError(err);
      lastErr = mapped;
      if (!shouldRetry(mapped) || attempt === maxRetries) throw mapped;
      await delay(retryDelayMs * Math.pow(2, attempt));
      attempt += 1;
    }
  }
  // Unreachable — the loop either returns or throws — but satisfy TS.
  throw lastErr ?? new MediaUploadError('network', 0, 'Upload failed.');
}
